import { Fragment } from "react";
import { groupInvoiceLines } from "../lib/invoice-line-groups";
import { isCustomerVisibleInvoiceLine, type InvoiceLineVisibility } from "../lib/invoice-line-visibility";
import { InvoiceLineDescription } from "./InvoiceLineDescription";

type Line = Parameters<typeof InvoiceLineDescription>[0]["line"] & InvoiceLineVisibility & {
  id: string;
  priceBookItemId?: string | null;
  groupName?: string | null;
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
};
type Item = { id: string; categoryId: string; name: string; description?: string | null; modifierType?: string | null };
type Category = { id: string; name: string; kind: string };
const money = (cents: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);

/** The invoice page and job card use the same groups, columns and descriptions. */
export function InvoiceItemsTable({ lines, items = [], categories = [], onEdit }: {
  lines: Line[];
  items?: Item[];
  categories?: Category[];
  onEdit?: () => void;
}) {
  if (!lines.length) return null;
  const columns = onEdit ? 5 : 4;
  const catalog = new Map(items.map(item => [item.id, item]));
  return <table className="w-full text-sm job-invoice-lines invoice-items-table" aria-label="Invoice charges">
    <thead><tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
      <th className="pb-1">Description</th><th className="pb-1 text-right">Quantity</th><th className="pb-1 text-right">Unit price</th><th className="pb-1 text-right">Line total</th>
      {onEdit && <th className="w-12"><span className="sr-only">Line actions</span></th>}
    </tr></thead>
    <tbody className="divide-y divide-[var(--line)]">
      {groupInvoiceLines(lines, items, categories).map(([group, groupLines]) => <Fragment key={group}>
        <tr><th colSpan={columns} scope="colgroup" className="pb-2 pt-4 text-left text-xs font-semibold text-[var(--ink-soft)]">{group === "Labor / install" ? "Services" : group}</th></tr>
        {groupLines.map(line => <tr key={line.id}>
          <td className="py-1.5 pr-2 text-[var(--ink)]"><InvoiceLineDescription line={line} catalog={line.priceBookItemId ? catalog.get(line.priceBookItemId) : undefined} />
            {!isCustomerVisibleInvoiceLine(line) && <span className="mt-1 inline-block rounded bg-[var(--wash)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--ink-soft)]">Hidden from customer</span>}
          </td>
          <td className="py-1.5 text-right text-[var(--ink-soft)]">{line.quantity}</td>
          <td className="py-1.5 text-right text-[var(--ink-soft)]">{money(line.unitPriceCents)}</td>
          <td className="py-1.5 text-right font-semibold text-[var(--ink)]">{money(line.totalCents)}</td>
          {onEdit && <td className="py-1.5 text-right"><button type="button" onClick={onEdit} className="text-xs font-semibold text-[var(--accent)]">Edit</button></td>}
        </tr>)}
      </Fragment>)}
    </tbody>
  </table>;
}
